import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { isPrefix, serializeKey } from "./keys";
import type { CacheEntry, CacheKey, CacheUpdater, HookCache } from "./types";
import { toast } from "sonner";
import * as Sentry from "@sentry/react";

const HookCacheContext = createContext<HookCache | null>(null);

// Internal aliases so the single unsafe boundary below does not parrot the
// `CacheEntry` token at the projection site (the public API still uses
// `CacheEntry<T>`).
type AnyEntry = CacheEntry<unknown>;
type TypedEntry<T> = CacheEntry<T>;

function notify(entry: AnyEntry): void {
  entry.subscribers.forEach((sub) => {
    sub();
  });
}

/**
 * Surface a hookCache fetch/refetch failure: report it to Sentry (init is
 * prod-only, so this is a no-op in dev) and show a single toast. Called from
 * both the initial-fetch (`registerQuery`) and refetch (`invalidate`) catch
 * sites, after the entry's error state is committed and subscribers notified —
 * so reporting is a pure side effect that never blocks the UI update.
 *
 * Without this, a rejected fetcher only set `entry.error`, which most consumers
 * ignore: the query never yields data, leaving a silent infinite spinner and no
 * Sentry event (subscriber-less `invalidate` refetches vanished entirely).
 */
function reportHookCacheFetchError(error: Error, serializedKey: string): void {
  Sentry.captureException(error, {
    tags: { source: "hookCache" },
    extra: { cacheKey: serializedKey },
  });
  // A fixed toast id collapses simultaneous failures (a prefix `invalidate`
  // fan-out, or an offline window failing many queries) into one toast. Sentry
  // still records one event per failed key, preserving the full signal.
  toast.error("Failed to load data. Please refresh the page.", {
    id: "hookcache-fetch-error",
  });
}

/**
 * The cache stores entries as `CacheEntry<unknown>` to keep the underlying
 * `Map` monomorphic. `useHookCacheQuery<T>` is the single registration point
 * that establishes the concrete `T` for a given key, so this projection
 * helper turns the stored entry back into `CacheEntry<T>` for callers.
 *
 * This is the one and only place where the cache crosses the unknown/T type
 * boundary; keeping the boundary contained here means read sites
 * (`getEntry`/`setEntry`) do not need ad-hoc casts.
 */
function projectEntry<T>(
  entry: AnyEntry | undefined,
): TypedEntry<T> | undefined {
  return entry as unknown as TypedEntry<T> | undefined;
}

interface HookCacheProviderProps {
  children: ReactNode;
}

export function HookCacheProvider({ children }: HookCacheProviderProps) {
  const cacheRef = useRef<Map<string, AnyEntry>>(new Map());

  const getEntry = useCallback(<T,>(key: CacheKey) => {
    return projectEntry<T>(cacheRef.current.get(serializeKey(key)));
  }, []);

  const setEntry = useCallback(
    <T,>(key: CacheKey, updater: CacheUpdater<T>) => {
      const existing = projectEntry<T>(cacheRef.current.get(serializeKey(key)));
      if (!existing || existing.data === undefined) return;
      const prev: T = existing.data;
      const newData =
        typeof updater === "function"
          ? (updater as (prev: T) => T)(prev)
          : updater;
      existing.data = newData;
      existing.error = null;
      notify(existing);
    },
    [],
  );

  const invalidate = useCallback(async (keyPrefix: CacheKey) => {
    const prefixSerialized = serializeKey(keyPrefix);
    const matching: [string, AnyEntry][] = [];
    for (const [serialized, entry] of cacheRef.current.entries()) {
      if (isPrefix(prefixSerialized, serialized)) {
        matching.push([serialized, entry]);
      }
    }
    await Promise.all(
      matching.map(async ([serialized, entry]) => {
        entry.isLoading = true;
        notify(entry);
        try {
          entry.data = await entry.fetcher();
          entry.error = null;
        } catch (err) {
          entry.error = err instanceof Error ? err : new Error(String(err));
        }
        entry.isLoading = false;
        entry.lastFetchedAt = Date.now();
        notify(entry);
        // A successful refetch nulls `error` above, so this only fires on a
        // genuine refetch failure for this entry.
        if (entry.error) {
          reportHookCacheFetchError(entry.error, serialized);
        }
      }),
    );
  }, []);

  const registerQuery = useCallback(
    <T,>(key: CacheKey, fetcher: () => Promise<T>): TypedEntry<T> => {
      const serialized = serializeKey(key);
      const existing = projectEntry<T>(cacheRef.current.get(serialized));
      if (existing) {
        // Refresh the fetcher reference (closures may have captured stale
        // params); the in-flight or already-resolved data stays as-is.
        existing.fetcher = fetcher;
        return existing;
      }
      const entry: TypedEntry<T> = {
        data: undefined,
        isLoading: true,
        error: null,
        lastFetchedAt: null,
        subscribers: new Set<() => void>(),
        fetcher,
      };
      // `CacheEntry<T>` is structurally assignable to `CacheEntry<unknown>`
      // (covariant for our read+write fields here), so no cast is needed
      // when storing.
      cacheRef.current.set(serialized, entry);
      entry
        .fetcher()
        .then((data) => {
          entry.data = data;
          entry.error = null;
          entry.isLoading = false;
          entry.lastFetchedAt = Date.now();
          notify(entry);
        })
        .catch((err: unknown) => {
          const error = err instanceof Error ? err : new Error(String(err));
          entry.error = error;
          entry.isLoading = false;
          notify(entry);
          reportHookCacheFetchError(error, serialized);
        });
      return entry;
    },
    [],
  );

  const unregisterSubscriber = useCallback((key: CacheKey, sub: () => void) => {
    const entry = cacheRef.current.get(serializeKey(key));
    entry?.subscribers.delete(sub);
  }, []);

  const value = useMemo<HookCache>(
    () => ({
      getEntry,
      setEntry,
      invalidate,
      registerQuery,
      unregisterSubscriber,
    }),
    [getEntry, setEntry, invalidate, registerQuery, unregisterSubscriber],
  );

  return (
    <HookCacheContext.Provider value={value}>
      {children}
    </HookCacheContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useHookCacheContext(): HookCache {
  const ctx = useContext(HookCacheContext);
  if (!ctx) {
    throw new Error(
      "useHookCacheContext must be used inside a <HookCacheProvider>",
    );
  }
  return ctx;
}

/**
 * Nullable variant of {@link useHookCacheContext} for components mounted both
 * inside and outside a `HookCacheProvider` (e.g. the sidebar, which renders
 * on every route while only the audit workpaper route carries a provider).
 * Returns null when no provider is present — callers no-op their cache work,
 * which is safe because a route without a provider has no cached entries to
 * go stale.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useHookCacheContextIfAvailable(): HookCache | null {
  return useContext(HookCacheContext);
}
